import axios from 'axios'
import { formatHttpValidationErrorMessage } from '@/platform/http/httpValidationError'

const FALLBACK_HTML_ERROR_MESSAGE = 'An unexpected error occurred, please try again later.'

/**
 * Detect HTML error bodies without matching incidental angle brackets
 * (e.g. stack frames like `at Object.<anonymous>`).
 */
function looksLikeHtml(value: string): boolean {
  const trimmed = value.trim()
  if (/^<!DOCTYPE\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) {
    return true
  }
  return /<[a-z][a-z0-9]*\b[^>]*>/i.test(trimmed) && /<\/[a-z][a-z0-9]*>/i.test(trimmed)
}

function normalizeErrorText(text: string | null | undefined): string {
  return text?.replace(/\s+/g, ' ').trim() ?? ''
}

function stripHtmlTags(text: string): string {
  return text.replace(/<[^>]+>/g, ' ')
}

/**
 * SSR-safe HTML error extraction (no DOMParser). Matches the heading → body → title
 * priority used in the browser path so proxy/gateway pages read consistently.
 */
function stripHtmlFromErrorMessageWithoutDom(value: string): string {
  const headingMatch = value.match(/<h[1-3][^>]*>([\s\S]*?)<\/h[1-3]>/i)
  const headingText = normalizeErrorText(
    headingMatch?.[1] ? stripHtmlTags(headingMatch[1]) : undefined,
  )
  if (headingText) {
    return headingText
  }

  const bodyMatch = value.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
  const bodyText = normalizeErrorText(stripHtmlTags(bodyMatch?.[1] ?? value))
  if (bodyText) {
    return bodyText
  }

  const titleMatch = value.match(/<title[^>]*>([\s\S]*?)<\/title>/i)
  const titleText = normalizeErrorText(titleMatch?.[1] ? stripHtmlTags(titleMatch[1]) : undefined)
  return titleText || FALLBACK_HTML_ERROR_MESSAGE
}

/**
 * Strip HTML from error bodies (e.g. proxy/gateway pages) and return plain text.
 * Prefers heading text, then body text, then document title.
 *
 * Uses DOMParser in the browser only; during SSR a regex fallback applies the same
 * priority. Avoid putting HTML-bearing errors in client component state that is
 * rendered on the server—if regex and DOMParser ever disagree, hydration can mismatch.
 */
export function stripHtmlFromErrorMessage(value: string): string {
  if (!looksLikeHtml(value)) {
    return value
  }

  if (typeof window === 'undefined') {
    return stripHtmlFromErrorMessageWithoutDom(value)
  }

  const doc = new DOMParser().parseFromString(value, 'text/html')

  const headingText = normalizeErrorText(doc.querySelector('h1, h2, h3')?.textContent)
  if (headingText) {
    return headingText
  }

  const bodyText = normalizeErrorText(doc.body?.textContent)
  if (bodyText) {
    return bodyText
  }

  const titleText = normalizeErrorText(doc.querySelector('title')?.textContent)
  return titleText || FALLBACK_HTML_ERROR_MESSAGE
}

export function getHttpResponseStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  return undefined
}

export function isHttpNotFoundError(error: unknown): boolean {
  return getHttpResponseStatus(error) === 404
}

/**
 * Extract error message from various error types.
 * Prioritizes API response detail over generic error messages, and strips HTML.
 */
export const extractErrorMessage = (err: unknown, defaultMessage: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data
    if (typeof data === 'string') {
      const stripped = stripHtmlFromErrorMessage(data).trim()
      if (stripped) {
        return stripped
      }
    } else {
      const formatted = formatHttpValidationErrorMessage(data?.detail)
      if (formatted) {
        return formatted
      }
    }
    if (err.message) {
      return stripHtmlFromErrorMessage(err.message)
    }
  } else if (err instanceof Error) {
    return stripHtmlFromErrorMessage(err.message)
  } else if (typeof err === 'string') {
    return stripHtmlFromErrorMessage(err)
  }

  return defaultMessage
}
